/**
 * CL-DRAWINGS-007B-R1 — Taxonomy-scoped drawing order editor (pure).
 */

import { compareDrawingFiles, isArchivedOrDeletedDrawing } from "./drawingOrderModel";
import {
  DRAWING_TAXONOMY_UNCATEGORIZED_LABEL,
  getEffectiveDrawingTaxonomy,
  groupDrawingsByTaxonomy,
  resolveDrawingTaxonomyPlacement,
  sortFilesWithinTaxonomyBucket,
} from "./drawingTaxonomyModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function flattenTaxonomyGroupedDrawings(grouped = {}) {
  const out = [];
  (grouped.categories || []).forEach((cat) => {
    (cat.subcategories || []).forEach((sub) => {
      (sub.files || []).forEach((file) => out.push(file));
    });
  });
  return out;
}

export function sortDrawingsForTaxonomyPresentation(files = [], taxonomy = {}, { project = null, includeExcluded = false } = {}) {
  const list = (Array.isArray(files) ? files : []).filter((f) => {
    if (!f) return false;
    if (includeExcluded) return true;
    return !isArchivedOrDeletedDrawing(f);
  });
  const grouped = groupDrawingsByTaxonomy(list, taxonomy, { project });
  return flattenTaxonomyGroupedDrawings(grouped);
}

/**
 * @returns {{ buckets: Array<{ categoryId, categoryLabel, subcategoryId, subcategoryLabel, fileIds: string[] }> }}
 */
export function buildTaxonomyDrawingOrderDraft(files = [], taxonomy = {}, { project = null } = {}) {
  const grouped = groupDrawingsByTaxonomy(files, taxonomy, { project });
  const buckets = [];
  grouped.categories.forEach((cat) => {
    cat.subcategories.forEach((sub) => {
      buckets.push({
        categoryId: cat.id,
        categoryLabel: cat.label,
        subcategoryId: sub.id,
        subcategoryLabel: sub.label,
        fileIds: sub.files.map((f) => safeStr(f.id)).filter(Boolean),
      });
    });
  });
  return { buckets };
}

export function findBucketIndex(draft, categoryId, subcategoryId) {
  return (draft?.buckets || []).findIndex(
    (b) => b.categoryId === categoryId && b.subcategoryId === subcategoryId
  );
}

export function moveDrawingInTaxonomyOrderDraft(draft, { categoryId, subcategoryId, fileId, direction = "up" } = {}) {
  const buckets = [...(draft?.buckets || [])].map((b) => ({ ...b, fileIds: [...b.fileIds] }));
  const bi = findBucketIndex({ buckets }, categoryId, subcategoryId);
  if (bi < 0) return draft;
  const ids = buckets[bi].fileIds;
  const id = safeStr(fileId);
  const ix = ids.indexOf(id);
  if (ix < 0) return draft;
  const delta = direction === "up" ? -1 : 1;
  const j = ix + delta;
  if (j < 0 || j >= ids.length) return draft;
  const nextIds = [...ids];
  [nextIds[ix], nextIds[j]] = [nextIds[j], nextIds[ix]];
  buckets[bi] = { ...buckets[bi], fileIds: nextIds };
  return { buckets };
}

export function reorderDrawingInTaxonomyOrderDraft(
  draft,
  { categoryId, subcategoryId, fromIndex, toIndex } = {}
) {
  const buckets = [...(draft?.buckets || [])].map((b) => ({ ...b, fileIds: [...b.fileIds] }));
  const bi = findBucketIndex({ buckets }, categoryId, subcategoryId);
  if (bi < 0) return draft;
  const ids = buckets[bi].fileIds;
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= ids.length ||
    toIndex >= ids.length ||
    fromIndex === toIndex
  ) {
    return draft;
  }
  const nextIds = [...ids];
  const [moved] = nextIds.splice(fromIndex, 1);
  nextIds.splice(toIndex, 0, moved);
  buckets[bi] = { ...buckets[bi], fileIds: nextIds };
  return { buckets };
}

/** Per-bucket sequential drawingSortOrder values (0..n-1). */
export function normalizeTaxonomyDrawingOrderDraft(draft = {}) {
  const updates = [];
  (draft.buckets || []).forEach((bucket) => {
    bucket.fileIds.forEach((fileId, index) => {
      updates.push({
        fileId: safeStr(fileId),
        drawingSortOrder: index,
        categoryId: bucket.categoryId,
        subcategoryId: bucket.subcategoryId,
      });
    });
  });
  return updates;
}

export function flattenTaxonomyDrawingOrderDraft(draft = {}) {
  const ids = [];
  (draft.buckets || []).forEach((bucket) => {
    bucket.fileIds.forEach((id) => {
      const fid = safeStr(id);
      if (fid) ids.push(fid);
    });
  });
  return ids;
}

export function taxonomyDrawingOrderDraftEquals(a, b) {
  const na = normalizeTaxonomyDrawingOrderDraft(a || { buckets: [] });
  const nb = normalizeTaxonomyDrawingOrderDraft(b || { buckets: [] });
  if (na.length !== nb.length) return false;
  const mapB = new Map(nb.map((r) => [r.fileId, r.drawingSortOrder]));
  return na.every((r) => mapB.get(r.fileId) === r.drawingSortOrder);
}

export function resetTaxonomyBucketToAutomatic(files = [], taxonomy = {}, categoryId, subcategoryId) {
  const inBucket = (Array.isArray(files) ? files : []).filter((f) => {
    const p = resolveDrawingTaxonomyPlacement(f, taxonomy);
    return p.categoryId === categoryId && p.subcategoryId === subcategoryId;
  });
  const sorted = [...inBucket].sort(compareDrawingFiles);
  return sorted.map((f) => safeStr(f.id)).filter(Boolean);
}

export function applyAutomaticOrderToDraft(draft, files, taxonomy) {
  const buckets = (draft?.buckets || []).map((bucket) => ({
    ...bucket,
    fileIds: resetTaxonomyBucketToAutomatic(files, taxonomy, bucket.categoryId, bucket.subcategoryId),
  }));
  return { buckets };
}

export function projectUsesTaxonomyDrawingOrder(project = null, files = []) {
  if (project?.drawingTaxonomy?.saved === true) return true;
  return (Array.isArray(files) ? files : []).some((f) => typeof f?.drawingSortOrder === "number");
}

export function resolveTaxonomyForDrawingOrder(project, files) {
  return getEffectiveDrawingTaxonomy(project, files);
}
